export interface BrickRendering {
  type: "topdown" | "isometric";
  x: number;
  y: number;
  z: number;
  width: number;
  height: number;
  color: string;
  colorIndex: number;
  colorName: string;
  brickTypeName: string;
  rotation?: number;
}

export interface Rotation {
  x: number;
  y: number;
}

/**
 * A LEGO brick is 0.6 stud tall. A build layer therefore stacks 0.6 grid
 * units, while the source model it was voxelized from keeps its true height
 * (1 grid unit per stud) — so the brick build is honestly a little shorter
 * than the model it copies.
 */
export const BRICK_LAYER_HEIGHT = 0.6;

/**
 * Key light in build space (+x right, +y deep, +z up) — the original-mesh
 * renderer uses the same direction, so both views are lit alike.
 */
export const LIGHT: [number, number, number] = (() => {
  const v = [0.42, 0.26, 0.87];
  const len = Math.hypot(v[0], v[1], v[2]);
  return [v[0] / len, v[1] / len, v[2] / len];
})();

/**
 * The orbit camera — a true orthographic camera.
 *
 * `rotation.y` is the azimuth (a turntable around the build's vertical
 * axis); `rotation.x` is the pitch and drives the elevation, so dragging
 * up tips the camera over the model instead of squashing it flat. All three
 * axes are foreshortened equally, so the model's height renders true (the
 * old 2.5D projection drew it at half scale).
 *
 * Shared by the brick renderer and the original-mesh renderer, so both views
 * use one camera.
 */
export interface Camera {
  /** azimuth in radians */
  psi: number;
  /** elevation in radians (above the horizon) */
  epsilon: number;
  /** unit vector toward the screen's right */
  right: [number, number, number];
  /** unit vector toward the screen's top */
  up: [number, number, number];
  /** unit vector from the model toward the camera */
  view: [number, number, number];
  /** the camera's horizontal direction — decides which side faces show */
  horizon: [number, number];
}

// The app's default view: the exact isometric angle (atan(1/√2) ≈ 35.26°),
// which gives the classic 2:1 iso axes with all three foreshortened equally.
const DEFAULT_PITCH = -28;
const DEFAULT_ELEVATION = Math.atan(1 / Math.SQRT2);
const ELEVATION_PER_DEGREE = 0.8;
const MIN_ELEVATION = (6 * Math.PI) / 180;
const MAX_ELEVATION = (84 * Math.PI) / 180;

const clamp = (v: number, lo: number, hi: number) =>
  Math.max(lo, Math.min(hi, v));

export const makeCamera = (rotation: Rotation): Camera => {
  const psi = (rotation.y * Math.PI) / 180;
  const epsilon = clamp(
    DEFAULT_ELEVATION +
      (rotation.x - DEFAULT_PITCH) * ELEVATION_PER_DEGREE * (Math.PI / 180),
    MIN_ELEVATION,
    MAX_ELEVATION,
  );

  const cosE = Math.cos(epsilon);
  const sinE = Math.sin(epsilon);
  const cosP = Math.cos(psi);
  const sinP = Math.sin(psi);

  return {
    psi,
    epsilon,
    right: [sinP, -cosP, 0],
    up: [-sinE * cosP, -sinE * sinP, cosE],
    view: [cosE * cosP, cosE * sinP, sinE],
    horizon: [cosP, sinP],
  };
};

/** The camera used to fit a model into the canvas (stable while orbiting). */
export const FIT_CAMERA = makeCamera({ x: DEFAULT_PITCH, y: 40 });

export interface ProjectedPoint {
  x: number;
  y: number;
}

/**
 * Build-space point (screen px; `pz` is the height in px) → screen px.
 */
export const projectPoint = (
  cam: Camera,
  px: number,
  py: number,
  pz = 0,
): ProjectedPoint => ({
  x: px * cam.right[0] + py * cam.right[1] + pz * cam.right[2],
  y: -(px * cam.up[0] + py * cam.up[1] + pz * cam.up[2]),
});

/**
 * Camera depth — larger is nearer the viewer. Used for the painter's
 * algorithm: faces and bricks are painted far to near.
 */
export const cameraDepth = (
  cam: Camera,
  px: number,
  py: number,
  pz = 0,
): number =>
  px * cam.view[0] + py * cam.view[1] + pz * cam.view[2];

/** Camera depth of a brick's centre — larger is nearer the viewer. */
export const brickDepth = (
  cam: Camera,
  brick: BrickRendering,
  pixelSize: number,
): number =>
  cameraDepth(
    cam,
    brick.x * pixelSize + brick.width / 2,
    brick.y * pixelSize + brick.height / 2,
    (brick.z ?? 0) * BRICK_LAYER_HEIGHT * pixelSize,
  );

/**
 * The ellipse a circle in the build's horizontal plane becomes on screen:
 * the singular values of the plane → screen map give its radii, the
 * principal direction gives its tilt.
 */
const planeCircleEllipse = (
  cam: Camera,
): { rx: number; ry: number; angle: number } => {
  // The local x and y axes, projected (per unit length)
  const a = cam.right[0];
  const b = cam.right[1];
  const c = -cam.up[0];
  const d = -cam.up[1];

  const q11 = a * a + b * b;
  const q22 = c * c + d * d;
  const q12 = a * c + b * d;
  const trace = q11 + q22;
  const det = q11 * q22 - q12 * q12;
  const disc = Math.sqrt(Math.max(0, (trace * trace) / 4 - det));

  return {
    rx: Math.sqrt(Math.max(0, trace / 2 + disc)),
    ry: Math.sqrt(Math.max(0, trace / 2 - disc)),
    angle: 0.5 * Math.atan2(2 * q12, q11 - q22),
  };
};

export function shadeColor(hex: string, factor: number): string {
  const rgb = hex.match(/^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);
  if (!rgb) return hex;

  const channel = (c: string) =>
    Math.min(255, Math.round(parseInt(c, 16) * factor));

  return `rgb(${channel(rgb[1])}, ${channel(rgb[2])}, ${channel(rgb[3])})`;
}

export function renderBrick3D(
  brick: BrickRendering,
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  pixelSize: number,
  rotation: Rotation,
  /** Content-centering offset applied in screen space. */
  offset: { x: number; y: number } = { x: 0, y: 0 },
): void {
  const px = brick.x * pixelSize;
  const py = brick.y * pixelSize;
  const w = brick.width;
  const h = brick.height;

  const cam = makeCamera(rotation);
  // The layer stacks 0.6 stud per brick; the brick's own body is that tall
  const layerPx = BRICK_LAYER_HEIGHT * pixelSize;
  const topPx = (brick.z ?? 0) * layerPx + layerPx;

  // Yawed + projected top corners: A (near-left), B (near-right),
  // C (far-right), D (far-left)
  const A = projectPoint(cam, px, py, topPx);
  const B = projectPoint(cam, px + w, py, topPx);
  const C = projectPoint(cam, px + w, py + h, topPx);
  const D = projectPoint(cam, px, py + h, topPx);

  ctx.save();
  ctx.translate(centerX - offset.x, centerY - offset.y);

  // The camera's horizontal direction decides which vertical faces show
  const sides: Array<{
    edge: [ProjectedPoint, ProjectedPoint];
    normal: [number, number];
  }> = [];
  if (cam.horizon[0] > 0) {
    sides.push({ edge: [B, C], normal: [1, 0] });
  } else {
    sides.push({ edge: [A, D], normal: [-1, 0] });
  }
  if (cam.horizon[1] > 0) {
    sides.push({ edge: [C, D], normal: [0, 1] });
  } else {
    sides.push({ edge: [A, B], normal: [0, -1] });
  }

  // The face that catches the key light is the brighter one
  const lambert = (n: [number, number]) =>
    Math.max(0, n[0] * LIGHT[0] + n[1] * LIGHT[1]);
  sides.sort((f1, f2) => lambert(f1.normal) - lambert(f2.normal));

  // Side faces — the darker one first, then the lit one
  sides.forEach((side, index) => {
    const lit = index === sides.length - 1;
    const [p1, p2] = side.edge;
    // The face drops straight down by the brick's own height
    const drop = projectPoint(cam, 0, 0, -layerPx).y;
    const grad = ctx.createLinearGradient(p1.x, p1.y, p1.x, p1.y + drop);
    grad.addColorStop(0, shadeColor(brick.color, lit ? 0.88 : 0.74));
    grad.addColorStop(1, shadeColor(brick.color, lit ? 0.78 : 0.64));
    ctx.beginPath();
    ctx.moveTo(p1.x, p1.y);
    ctx.lineTo(p2.x, p2.y);
    ctx.lineTo(p2.x, p2.y + drop);
    ctx.lineTo(p1.x, p1.y + drop);
    ctx.closePath();
    ctx.fillStyle = grad;
    ctx.fill();
  });

  // Top face — light falls from the near corner toward the far one
  const topGrad = ctx.createLinearGradient(A.x, A.y, C.x, C.y);
  topGrad.addColorStop(0, shadeColor(brick.color, 1.05));
  topGrad.addColorStop(1, shadeColor(brick.color, 0.95));
  ctx.beginPath();
  ctx.moveTo(A.x, A.y);
  ctx.lineTo(B.x, B.y);
  ctx.lineTo(C.x, C.y);
  ctx.lineTo(D.x, D.y);
  ctx.closePath();
  ctx.fillStyle = topGrad;
  ctx.fill();
  ctx.strokeStyle = "rgba(0,0,0,0.18)";
  ctx.lineWidth = 1;
  ctx.stroke();

  // Studs on the top face — projected with the same camera, so they follow
  // the brick as it turns. A stud circle flattens into a tilted ellipse as
  // the camera rises.
  const spacing = Math.max(pixelSize, 8);
  const axes = planeCircleEllipse(cam);
  for (let sy = 0; sy < h / spacing; sy++) {
    for (let sx = 0; sx < w / spacing; sx++) {
      const stud = projectPoint(
        cam,
        px + sx * spacing + spacing / 2,
        py + sy * spacing + spacing / 2,
        topPx,
      );
      const radius = spacing * 0.3;

      // contact shadow → body → top-left highlight reads as a raised stud
      ctx.beginPath();
      ctx.ellipse(
        stud.x,
        stud.y + radius * 0.3,
        radius * 1.05 * axes.rx,
        radius * 1.05 * axes.ry,
        axes.angle,
        0,
        Math.PI * 2,
      );
      ctx.fillStyle = "rgba(0,0,0,0.14)";
      ctx.fill();

      ctx.beginPath();
      ctx.ellipse(
        stud.x,
        stud.y,
        radius * axes.rx,
        radius * axes.ry,
        axes.angle,
        0,
        Math.PI * 2,
      );
      ctx.fillStyle = shadeColor(brick.color, 0.98);
      ctx.fill();
      ctx.strokeStyle = "rgba(0,0,0,0.15)";
      ctx.lineWidth = 0.5;
      ctx.stroke();

      ctx.beginPath();
      ctx.ellipse(
        stud.x - radius * 0.25 * axes.rx,
        stud.y - radius * 0.25 * axes.ry,
        radius * 0.5 * axes.rx,
        radius * 0.5 * axes.ry,
        axes.angle,
        0,
        Math.PI * 2,
      );
      ctx.fillStyle = shadeColor(brick.color, 1.08);
      ctx.fill();
    }
  }

  ctx.restore();
}

export function renderBrick2D(
  brick: BrickRendering,
  ctx: CanvasRenderingContext2D,
  pixelSize: number,
): void {
  const px = brick.x * pixelSize;
  const py = brick.y * pixelSize;
  const w = brick.width;
  const h = brick.height;

  ctx.fillStyle = brick.color;
  ctx.fillRect(px, py, w, h);

  ctx.strokeStyle = "rgba(0, 0, 0, 0.4)";
  ctx.lineWidth = 1;
  ctx.strokeRect(px, py, w, h);
  const spacing = Math.max(pixelSize, 8);
  const studRadius = spacing * 0.3;
  for (let sx = 0; sx < brick.width / spacing; sx++) {
    for (let sy = 0; sy < brick.height / spacing; sy++) {
      const cx = px + sx * spacing + spacing / 2;
      const cy = py + sy * spacing + spacing / 2;
      // Same stud style as the on-screen mosaic (PixelBrickCanvas)
      ctx.beginPath();
      ctx.arc(cx, cy, studRadius, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(255, 255, 255, 0.18)";
      ctx.fill();
      ctx.strokeStyle = "rgba(0, 0, 0, 0.15)";
      ctx.lineWidth = 0.5;
      ctx.stroke();
    }
  }
}
